import React, { useState, useEffect, useRef } from "react";

// Mock translation hook for standalone demo
const useTranslation = () => ({ t: (key) => key });

const TimerComponent = ({ initialTime }) => {
  const [remainingTime, setRemainingTime] = useState(initialTime);
  const [flipping, setFlipping] = useState({
    day: false,
    hour: false,
    minute: false,
    second: false,
  });
  const prevTime = useRef(initialTime);
  const { t } = useTranslation();

  useEffect(() => {
    const decrementTime = () => {
      setRemainingTime((prev) => {
        let { day, hour, minute, second } = prev;
        const changed = {
          day: false,
          hour: false,
          minute: false,
          second: false,
        };

        if (second > 0) {
          second -= 1;
          changed.second = true;
        } else {
          changed.second = true;
          if (minute > 0) {
            minute -= 1;
            second = 59;
            changed.minute = true;
          } else {
            changed.minute = true;
            if (hour > 0) {
              hour -= 1;
              minute = 59;
              second = 59;
              changed.hour = true;
            } else {
              changed.hour = true;
              if (day > 0) {
                day -= 1;
                hour = 23;
                minute = 59;
                second = 59;
                changed.day = true;
              }
            }
          }
        }

        setFlipping(changed);
        setTimeout(
          () =>
            setFlipping({
              day: false,
              hour: false,
              minute: false,
              second: false,
            }),
          600,
        );

        if (day === 0 && hour === 0 && minute === 0 && second === 0)
          return prev;
        return { day, hour, minute, second };
      });
    };

    const interval = setInterval(decrementTime, 1000);
    return () => clearInterval(interval);
  }, []);

  const pad = (n) => String(n).padStart(2, "0");

  const units = [
    { key: "day", label: "Days", value: remainingTime.day },
    { key: "hour", label: "Hours", value: remainingTime.hour },
    { key: "minute", label: "Mintues", value: remainingTime.minute },
    { key: "second", label: "Seconds", value: remainingTime.second },
  ];

  return (
    <div className="timer-wrapper">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@300;400;700&family=Space+Mono:wght@400;700&display=swap');

        .timer-wrapper {
          position: relative;
          z-index: 1;
          text-align: center;
          padding: 20px 12px;
          width: 100%;
          max-width: 100%;
          margin: 0 auto;
          font-family: 'Space Mono', monospace;
        }

        .timer-cards {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          gap: 8px;
          justify-content: center;
          align-items: center;
          width: 100%;
        }

        .timer-card {
          position: relative;
          flex: 1 1 0;
          min-width: 0;
          max-width: none;
        }

        .card-inner {
          position: relative;
          background: rgba(20, 20, 30, 0.6);
          border: 1px solid rgba(255, 107, 0, 0.2);
          border-radius: 12px;
          padding: 12px 4px 8px 4px;
          backdrop-filter: blur(10px);
          overflow: hidden;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          width: 100%;
        }

        .card-inner::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 2px;
          background: linear-gradient(90deg, transparent, #ff6b00, #ffaa00, #ff6b00, transparent);
          transition: left 0.5s ease;
        }

        .timer-card.flipping .card-inner::before {
          left: 100%;
        }

        .card-inner::after {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 50% 0%, rgba(255,107,0,0.2), transparent 70%);
          opacity: 0;
          transition: opacity 0.3s;
          pointer-events: none;
        }

        .timer-card.flipping .card-inner {
          border-color: #ff6b00;
          box-shadow: 0 0 20px rgba(255, 107, 0, 0.3);
          transform: scale(1.02);
        }

        .timer-card.flipping .card-inner::after {
          opacity: 1;
        }

        .card-glow {
          position: absolute;
          inset: 0;
          border-radius: 12px;
          background: radial-gradient(circle at 50% 50%, rgba(255,107,0,0.25), transparent 80%);
          opacity: 0;
          transition: opacity 0.3s;
          pointer-events: none;
        }

        .timer-card.flipping .card-glow {
          opacity: 1;
          animation: glowPulse 0.6s ease-out;
        }

        @keyframes glowPulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.8; }
        }

        .card-value {
          font-family: 'Oswald', sans-serif;
          font-weight: 700;
          font-size: clamp(1.5rem, 5vw, 2.5rem);
          line-height: 1;
          color: #fff;
          letter-spacing: -0.02em;
          position: relative;
          display: block;
          text-shadow: 0 0 20px rgba(255,255,255,0.3);
          white-space: nowrap;
        }

        .timer-card.flipping .card-value {
          color: #ff6b00;
          animation: flipPulse 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
          text-shadow: 0 0 30px #ff6b00;
        }

        @keyframes flipPulse {
          0% { 
            transform: scaleY(1) translateY(0);
          }
          30% { 
            transform: scaleY(0.7) translateY(-3px);
          }
          70% { 
            transform: scaleY(1.1) translateY(2px);
          }
          100% { 
            transform: scaleY(1) translateY(0);
          }
        }

        .card-label {
          font-size: clamp(0.4rem, 2vw, 0.6rem);
          letter-spacing: 0.1em;
          color: rgba(255, 255, 255, 0.5);
          text-transform: uppercase;
          margin-top: 4px;
          display: block;
          font-family: 'Space Mono', monospace;
          font-weight: 400;
          white-space: nowrap;
        }

        .card-divider {
          position: absolute;
          top: 50%;
          left: 10%;
          right: 10%;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,107,0,0.3), transparent);
          transform: translateY(-50%);
          pointer-events: none;
        }

        .separator {
          font-family: 'Oswald', sans-serif;
          font-size: clamp(1rem, 4vw, 2rem);
          font-weight: 300;
          color: #ff6b00;
          align-self: center;
          line-height: 1;
          flex: 0 0 auto;
          animation: blink 1s step-end infinite;
          text-shadow: 0 0 20px #ff6b00;
          margin: 0 2px;
        }

        @keyframes blink {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.1); }
        }

        /* Responsive adjustments */
        @media (max-width: 480px) {
          .timer-wrapper {
            padding: 16px 8px;
          }
          
          .timer-cards {
            gap: 4px;
          }
          
          .card-inner {
            padding: 8px 2px 6px 2px;
            border-radius: 8px;
          }
          
          .card-value {
            font-size: clamp(1.2rem, 4vw, 1.8rem);
          }
          
          .card-label {
            font-size: clamp(0.35rem, 1.5vw, 0.5rem);
            letter-spacing: 0.05em;
          }
          
          .separator {
            font-size: clamp(0.8rem, 3vw, 1.5rem);
            margin: 0 1px;
          }
        }

        @media (max-width: 360px) {
          .timer-cards {
            gap: 2px;
          }
          
          .card-inner {
            padding: 6px 1px 4px 1px;
          }
          
          .card-value {
            font-size: clamp(1rem, 3.5vw, 1.5rem);
          }
          
          .card-label {
            font-size: clamp(0.3rem, 1.2vw, 0.45rem);
          }
        }

        @media (max-width: 320px) {
          .card-value {
            font-size: clamp(0.9rem, 3vw, 1.2rem);
          }
          
          .card-label {
            font-size: clamp(0.25rem, 1vw, 0.4rem);
          }
        }

        /* For very large screens */
        @media (min-width: 768px) {
          .timer-cards {
            gap: 12px;
          }
          
          .card-inner {
            padding: 16px 8px 12px 8px;
          }
          
          .card-value {
            font-size: clamp(2rem, 4vw, 3rem);
          }
          
          .card-label {
            font-size: clamp(0.6rem, 1.2vw, 0.8rem);
          }
        }
      `}</style>

      {/* Timer Cards */}
      <div className="timer-cards">
        {units.map((unit, index) => (
          <React.Fragment key={unit.key}>
            <div
              className={`timer-card ${flipping[unit.key] ? "flipping" : ""}`}
            >
              <div className="card-glow" />
              <div className="card-inner">
                <div className="card-divider" />
                <span className="card-value">{pad(unit.value)}</span>
                <span className="card-label">{t(unit.label)}</span>
              </div>
            </div>
            {index < units.length - 1 && <span className="separator">:</span>}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default TimerComponent;
